import React, { startTransition, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { CAccordion, CAccordionBody, CAccordionHeader, CAccordionItem, CRow, CFormLabel, CButton, CTable, CTableBody, CTableDataCell, CTableRow } from '@coreui/react'
import Modal from '../../components/shared/ModalTable'
import './Buildings.css'
import SavePatients from '../../services/general/savePatientinformation.api'
import PropTypes from 'prop-types'
import Button from '@mui/material/Button'
import SaveExtra from '../../services/general/saveExtraData.api'
import { LogIn, SearchIcon, X } from 'lucide-react'
import { FunnelPlotOutlined } from '@ant-design/icons'
import AppSelect from '../../components/shared/AppSelect'
import Pageloader from '../../components/shared/Pageloader'
import fetchPatientData from '../../services/general/patientGeneralAPI.api'
import FacilityCRUD from '../../services/general/getFacilityData.api'
import CustomDatePickingFormat from '../../components/shared/CustomDatePickingFormat'
import { getAllAppointmentsDetails } from '../../components/shared/appointmentFilterData'
import './dashboard.css'

const selectStatusOption = [
  { value: 'NewPatient', label: 'New Patient' },
  { value: 'Follow Up', label: 'Follow Up' },
  { value: 'Re Admit', label: 'Re Admit' },
]

const selectAppointmentStatus = [
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'Rescheduled', label: 'Rescheduled' },
  { value: 'Canceled', label: 'Canceled' },
]

const safeJsonParse = (value, fallback = null) => {
  if (value === undefined || value === null || value === '') return fallback
  if (typeof value !== 'string') return value

  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback
  }
}

const normalizeDateValue = (date) => {
  if (!date) return null

  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) return null

  return parsedDate.toISOString().split('T')[0]
}

const formatDateToMMDDYYYYLocal = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()

  return `${month}/${day}/${year}`
}

const getTodayDateFilterValue = () => formatDateToMMDDYYYYLocal(new Date())

const buildOptionLookup = (options, keySelector) =>
  options.reduce((accumulator, option) => {
    accumulator[keySelector(option)] = option
    return accumulator
  }, {})

const statusOptionsByValue = buildOptionLookup(selectStatusOption, (option) => option.value)
const appointmentStatusOptionsByValue = buildOptionLookup(selectAppointmentStatus, (option) => option.value)

const getPatientFullName = (patient) => [patient?.FirstName, patient?.MiddleName, patient?.LastName].filter(Boolean).join(' ')

const getAppointmentTypeLabel = (appointmentType) => {
  if (appointmentType === 'Follow Up') return 'Follow Up'
  if (appointmentType === 'New') return 'New Patient'
  if (appointmentType === 'Re Admit') return 'Re Admit'
  return appointmentType || ''
}

const CallCenterFilterPanel = React.memo(function CallCenterFilterPanel({
  showFilter,
  facilities,
  facilityOptionsById,
  filterData,
  onClose,
  onFilterChange,
  providerOptions,
  providerOptionsByName,
}) {
  return (
    <div
      style={{
        width: '400px',
        zIndex: 50,
        opacity: showFilter ? 1 : 0,
        transform: showFilter ? 'translateY(0)' : 'translateY(4px)',
        pointerEvents: showFilter ? 'auto' : 'none',
        visibility: showFilter ? 'visible' : 'hidden',
        transition: 'opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease',
      }}
      className="position-absolute gap-1 d-flex flex-column top-full left-0 z-10 p-4 patient-theme-filter-popover dashboard-dark-filter-popover"
    >
      <div>
        <CFormLabel>Select Facility</CFormLabel>
        <AppSelect
          placeholder="Select"
          name="facility"
          options={facilities}
          value={filterData.facility.map((value) => facilityOptionsById[value]).filter(Boolean)}
          isMulti
          isClearable
          onChange={(selectedOptions) =>
            onFilterChange((prev) => ({
              ...prev,
              facility: selectedOptions ? selectedOptions.map((option) => option.value) : [],
            }))
          }
        />
      </div>

      <div className="mt-2">
        <CFormLabel>Select Patient Status</CFormLabel>
        <AppSelect
          name="status"
          isMulti
          placeholder="Select"
          options={selectStatusOption}
          isClearable
          value={filterData.status.map((value) => statusOptionsByValue[value]).filter(Boolean)}
          onChange={(selectedOptions) =>
            onFilterChange((prev) => ({
              ...prev,
              status: selectedOptions ? selectedOptions.map((option) => option.value) : [],
            }))
          }
        />
      </div>
      <div className="mt-2">
        <CFormLabel>Select Appointment Status</CFormLabel>
        <AppSelect
          name="status"
          isMulti
          placeholder="Select"
          options={selectAppointmentStatus}
          isClearable
          value={filterData.appointmentStatus.map((value) => appointmentStatusOptionsByValue[value]).filter(Boolean)}
          onChange={(selectedOptions) =>
            onFilterChange((prev) => ({
              ...prev,
              appointmentStatus: selectedOptions ? selectedOptions.map((option) => option.value) : [],
            }))
          }
        />
      </div>

      <div className="mt-2">
        <CFormLabel>Select Providers</CFormLabel>
        <AppSelect
          name="status"
          isMulti
          placeholder="Select Providers"
          isClearable
          options={providerOptions}
          value={filterData.providers.map((value) => providerOptionsByName[value]).filter(Boolean)}
          onChange={(selectedOptions) =>
            onFilterChange((prev) => ({
              ...prev,
              providers: selectedOptions ? selectedOptions.map((option) => option.value) : [],
            }))
          }
        />
      </div>

      <div className="mt-2" style={{ position: 'relative', display: 'inline-block' }}>
        <CFormLabel>Select DOB</CFormLabel>
        <CustomDatePickingFormat
          value={filterData.date || ''}
          aria-describedby="exampleFormControlInputHelpInline"
          onChange={(e) => {
            onFilterChange((p) => ({
              ...p,
              date: e,
            }))
          }}
        />
      </div>

      <div className="mt-2" style={{ position: 'relative', display: 'inline-block' }}>
        <CFormLabel>Select Scheduled Date</CFormLabel>
        <CustomDatePickingFormat
          value={filterData.scheduleDate || ''}
          aria-describedby="exampleFormControlInputHelpInline"
          onChange={(e) => {
            onFilterChange((p) => ({
              ...p,
              scheduleDate: e,
            }))
          }}
        />
      </div>

      <Button className="mt-2 patient-theme-filter-button" onClick={onClose} variant="outlined">
        Close
      </Button>
    </div>
  )
})

CallCenterFilterPanel.propTypes = {
  showFilter: PropTypes.bool.isRequired,
  facilities: PropTypes.array.isRequired,
  facilityOptionsById: PropTypes.object.isRequired,
  filterData: PropTypes.shape({
    facility: PropTypes.array.isRequired,
    scheduleDate: PropTypes.string.isRequired,
    searchValue: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    status: PropTypes.array.isRequired,
    providers: PropTypes.array.isRequired,
    appointmentStatus: PropTypes.array.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  providerOptions: PropTypes.array.isRequired,
  providerOptionsByName: PropTypes.object.isRequired,
}

const CallCenterAppointmentList = React.memo(function CallCenterAppointmentList({ groupedAppointments, patientClick }) {
  return (
    <div className="mt-3 all-charts-list call-center-schedule-list">
      <CAccordion alwaysOpen className="border-bottom-0 m-0 w-100 border-0 p-0 dashboard-dark-accordion patient-theme-accordion all-charts-accordion" style={{ borderRadius: '10px' }}>
        {Object.entries(groupedAppointments).map(([date, providers], dateIndex) => (
          <div className="testdiv mb-0" key={date}>
            <div className="border-bottom-0 border-0 mb-0 call-center-date-heading">{date}</div>

            <div className="border-bottom-0 border-to-add-facility card-header bg-none d-flex w-100 border-0 p-0 shadow-sm all-charts-provider-group call-center-provider-group" style={{ borderRadius: '40px!important' }}>
              <CAccordion alwaysOpen className="border-bottom-0 m-0 w-100 border-0 p-0 dashboard-dark-accordion patient-theme-accordion all-charts-accordion" style={{ borderRadius: '10px' }}>
                {Object.entries(providers).map(([providerName, providerData], providerIndex) => (
                  <CAccordionItem className="border-0 border-bottom m-0 dashboard-dark-facility-item patient-theme-facility-item all-charts-provider-item call-center-provider-item" itemKey={`${dateIndex}-${providerIndex}`} key={`${date}-${providerName}`}>
                    <CAccordionHeader className="patient-theme-accordion-header">
                      {providerName} ({providerData?.count || 0})
                    </CAccordionHeader>
                    <CAccordionBody>
                      <CAccordion alwaysOpen className="border-bottom-0 m-0 w-100 border-0 p-0 dashboard-dark-accordion patient-theme-accordion all-charts-accordion" style={{ borderRadius: '10px' }}>
                        {Object.entries(providerData?.facilities || {}).map(([facilityName, facilityPatients], facilityIndex) => (
                          <CAccordionItem disabled className="all-charts-facility-item dashboard-dark-facility-item patient-theme-facility-item border-bottom-0 m-0 shadow-sm border call-center-facility-item" style={{ borderRadius: '10px' }} key={`${date}-${providerName}-${facilityName}-${facilityIndex}`}>
                            <CAccordionHeader className="all-charts-facility-header call-center-facility-header" style={{ fontSize: '14px', boxShadow: 'unset' }}>
                              {facilityName}
                            </CAccordionHeader>
                            <CAccordionBody className="all-charts-facility-body border-bottom-0 border-0 py-0 p-0 call-center-facility-body">
                              <CTable className="all-charts-patient-table patient-theme-table mb-0 border-bottom-0 border-0 call-center-patient-table">
                                <CTableBody>
                                  {(facilityPatients || []).map(({ patient, scheduleDate, appointmentType, appointmentStatus }, patientIndex) => {
                                    const appointmentLabel = getAppointmentTypeLabel(appointmentType)

                                    return (
                                      <CTableRow
                                        className={`all-charts-patient-row dashboard-dark-patient-row order-0 px-4 ${patient.Status === 'NewPatient' ? 'NP' : patient.Status === 'Follow Up' ? 'FU' : patient.Status} patient-row-list border-bottom-0 border-0 call-center-patient-row`}
                                        key={`${patient.PatientID}-${scheduleDate || 'na'}-${patientIndex}`}
                                        style={{ verticalAlign: 'middle' }}
                                      >
                                        <CTableDataCell className="border-bottom-0 border-0 call-center-patient-cell" scope="row" style={{ width: '100%' }}>
                                          <div className="call-center-row-layout">
                                            <span className="call-center-patient-name-wrap">
                                              <span className="patient-name-fixed">{getPatientFullName(patient)}</span>
                                              {/* {appointmentLabel && (
                                                <span
                                                  className={`patient-theme-status-badge ${appointmentType === 'New'
                                                    ? 'patient-theme-status-badge--new'
                                                    : appointmentType === 'Follow Up'
                                                      ? 'patient-theme-status-badge--followup'
                                                      : 'patient-theme-status-badge--readmit'}`}
                                                >
                                                  ({appointmentLabel})
                                                </span>
                                              )} */}
                                            </span>
                                            <div className="call-center-row-pills">
                                              <span className="room-pill patient-theme-fixed-pill">Room No: {patient?.Room || '-'}</span>
                                              <span className="notestatus-pill patient-theme-fixed-pill">Appointment: {appointmentStatus || '-'}</span>
                                              <span className="schedule-pill patient-theme-fixed-pill">Scheduled Date: {scheduleDate || '-'}</span>
                                            </div>
                                          </div>
                                        </CTableDataCell>

                                        <CTableDataCell className="d-flex justify-content-end border-bottom-0 border-0 call-center-action-cell">
                                          <div className="card-header all-charts-row-actions border-0 pb-0 m-0 p-0 d-flex align-items-center gap-2">
                                            <CButton
                                              onClick={() => patientClick?.(patient)}
                                              variant="outline"
                                              color="primary"
                                              className="px-2 patient-theme-icon-button call-center-row-open-button"
                                            >
                                              <LogIn size={20} strokeWidth={2} />
                                            </CButton>
                                          </div>
                                        </CTableDataCell>
                                      </CTableRow>
                                    )
                                  })}
                                </CTableBody>
                              </CTable>
                            </CAccordionBody>
                          </CAccordionItem>
                        ))}
                      </CAccordion>
                    </CAccordionBody>
                  </CAccordionItem>
                ))}
              </CAccordion>
            </div>
          </div>
        ))}
      </CAccordion>
    </div>
  )
})

CallCenterAppointmentList.propTypes = {
  groupedAppointments: PropTypes.object.isRequired,
  patientClick: PropTypes.func,
}

export const CallCenter = ({ patientClick }) => {
  const buttonList = []
  const [patientData, setPatientData] = useState('')
  const [allStatus, setAllStatus] = useState(['Loading...', 'Loading...', 'Loading...', 'Loading...', 'Loading...'])
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [modalVisible, setModalVisible] = useState(false)
  const [patientModelVisible, setPatientModalVisible] = useState(false)
  const [modalData, setModalData] = useState('')
  const [patientIDClicked, setPatientIDClicked] = useState(-1)
  const [rowClicked, setRowClicked] = useState(null)
  const [facilities, setFacilities] = useState([])
  const [facilityOptionsById, setFacilityOptionsById] = useState({})
  const [showFilter, setShowFilter] = useState(false)
  const [filterData, setFilterData] = useState({
    facility: [],
    scheduleDate: getTodayDateFilterValue(),
    searchValue: '',
    date: '',
    status: [],
    providers: [],
    appointmentStatus: [],
  })

  const [appointment, setAppointment] = useState([])
  const [filteredPatientData, setFilteredPatientData] = useState([])
  const [providerOptions, setProviderOptions] = useState([])
  const [providerOptionsByName, setProviderOptionsByName] = useState({})
  const deferredSearchValue = useDeferredValue(filterData.searchValue)
  const appointmentRequestRef = useRef(0)

  const patientDataFn = async (scheduleDate = filterData?.scheduleDate || getTodayDateFilterValue()) => {
    const requestId = appointmentRequestRef.current + 1
    appointmentRequestRef.current = requestId
    setIsPageLoading(true)
    try {
      const [patientDataResponse, appointments] = await Promise.all([SavePatients('GetAll', '', '', ''), getAppointmentData(scheduleDate)])

      if (requestId !== appointmentRequestRef.current) return

      const patient = safeJsonParse(patientDataResponse?.d, {})?.Data || []
      const patientIdsWithAppointments = new Set(appointments.map((item) => String(item?.current?.Patient?.value ?? item?.data?.PatientID ?? '')))
      const schedulePatient = patient.filter((patientItem) => patientIdsWithAppointments.has(String(patientItem.PatientID)))
      setPatientData(schedulePatient)
      setAppointment(appointments)
    } catch (error) {
      console.log(error)
    } finally {
      if (requestId === appointmentRequestRef.current) {
        setIsPageLoading(false)
      }
    }
  }

  const handleFilterChange = useCallback((updater) => {
    startTransition(() => {
      setFilterData(updater)
    })
  }, [])

  const toggleFilter = useCallback(() => {
    startTransition(() => {
      setShowFilter((previous) => !previous)
    })
  }, [])

  const closeFilter = useCallback(() => {
    startTransition(() => {
      setShowFilter(false)
    })
  }, [])

  const getAppointmentData = async (scheduleDate) => {
    try {
      const appointmentDetails = scheduleDate ? await getAllAppointmentsDetails('GETBYSCHEDULEDATE', scheduleDate, '') : await getAllAppointmentsDetails('GET')

      let flatAppointments = (appointmentDetails || []).map((appointment) => {
        const data = appointment?.data || {}
        const current = appointment?.current || {
          Patient: { value: data?.PatientID || '', label: data?.AppointmentName || 'Unknown' },
          Provider: { value: '', label: data?.ProviderName || 'Unknown' },
          Facility: { value: '', label: data?.Facility || 'Unknown' },
          Insurance: data?.Insurance || {},
          otherDetails: data?.otherDetails || {},
        }

        return { data, current }
      })
      flatAppointments = flatAppointments.filter((item) => item.data.Status === 'Scheduled' || item?.data?.Status === 'Rescheduled')

      return flatAppointments
    } catch (error) {
      console.log('Error in getAppointmentData', error)
      return []
    }
  }
  useEffect(() => {
    if (!Array.isArray(patientData) || patientData.length === 0) {
      setFilteredPatientData([])
      return
    }

    const updatedPatients = patientData.map((patient) => {
      const findAppointment = appointment.find((item) => item?.data?.PatientID === patient?.PatientID && (item?.data?.Status === 'Scheduled' || item?.data?.Status === 'Rescheduled'))

      return {
        ...patient,
        providerAssigned: findAppointment?.current?.Provider?.label,
        scheduleStartTime: findAppointment?.data?.StartTime,
        appointmentStatus: findAppointment?.data?.Status,
      }
    })

    setFilteredPatientData(updatedPatients)
  }, [appointment, patientData])

  const patientSelected = async (type, rowSelected) => {
    setPatientIDClicked(rowSelected?.PatientID)
    setRowClicked(rowSelected)
    setPatientModalVisible(true)
  }
  const getAllProviderData = async () => {
    try {
      // Fetch provider data
      let providerData = await fetchPatientData('SELECT', 'Provider', '{}')
      providerData = safeJsonParse(providerData?.d, {})?.Data || []
      const formattedProviderOptions = providerData.map((provider) => ({
        value: provider.Name,
        label: provider.Name,
      }))
      setProviderOptions(formattedProviderOptions)
      setProviderOptionsByName(buildOptionLookup(formattedProviderOptions, (option) => option.value))
    } catch (error) {
      console.log(error)
    } finally {
    }
  }
  const getFacilities = async () => {
    let a = await FacilityCRUD('SELECT', {})
    let data = safeJsonParse(a?.d, {})?.Data || []
    try {
      const formattedData = data.map((facility) => ({
        value: facility.ID,
        label: facility.Name,
      }))
      setFacilities(formattedData)
      setFacilityOptionsById(buildOptionLookup(formattedData, (option) => option.value))
      // setSelectedFacility(formattedData[0])
    } catch (e) {}
  }

  useEffect(() => {
    getAllProviderData()
    getFacilities()
  }, [])

  useEffect(() => {
    patientDataFn(filterData.scheduleDate)
  }, [filterData.scheduleDate])

  const updatedFilteredPatients = useMemo(() => {
    if (!Array.isArray(filteredPatientData) || filteredPatientData.length === 0) {
      return []
    }

    const selectedFacilityLabels = new Set(facilities.filter((facility) => filterData.facility.includes(facility.value)).map((facility) => facility.label))
    const selectedProviders = new Set(filterData.providers)
    const selectedStatuses = new Set(filterData.status)
    const selectedAppointmentStatuses = new Set(filterData.appointmentStatus)
    const normalizedSearchValue = deferredSearchValue.trim().toLowerCase()
    const normalizedScheduleDate = normalizeDateValue(filterData.scheduleDate)
    
    return filteredPatientData.filter((patient) => {
      const firstName = patient?.FirstName?.toLowerCase() || ''
      const middleName = patient?.MiddleName?.toLowerCase() || ''
      const lastName = patient?.LastName?.toLowerCase() || ''
      const fullName = getPatientFullName(patient).toLowerCase()
      const dob = patient?.DOB || ''
      const dobText = typeof dob === 'string' ? dob.toLowerCase() : ''
      const hasAppointmentForScheduleDate =
        !normalizedScheduleDate ||
        appointment.some((appt) => {
          const appointmentPatientID = appt?.current?.Patient?.value
          const appointmentDate = appt?.data?.StartTime?.split?.(' ')?.[0] || appt?.data?.StartTime

          return appointmentPatientID === patient?.PatientID && normalizeDateValue(appointmentDate) === normalizedScheduleDate
        })

      const matchesFacility = selectedFacilityLabels.size === 0 || selectedFacilityLabels.has(patient?.FacilityName)
      const matchesProviders = selectedProviders.size === 0 || selectedProviders.has(patient?.providerAssigned)
      const matchesStatus = selectedStatuses.size === 0 || selectedStatuses.has(patient?.Status)
      const matchesSearchValue =
        !normalizedSearchValue ||
        firstName.includes(normalizedSearchValue) ||
        middleName.includes(normalizedSearchValue) ||
        lastName.includes(normalizedSearchValue) ||
        fullName.includes(normalizedSearchValue) ||
        dobText.includes(normalizedSearchValue)
      const matchesDate = !filterData.date || patient?.DOB === filterData.date
      const matchesSchedule = hasAppointmentForScheduleDate
      const matchesAppointmentStatus = selectedAppointmentStatuses.size === 0 || selectedAppointmentStatuses.has(patient?.appointmentStatus)

      return matchesFacility && matchesStatus && matchesSearchValue && matchesDate && matchesProviders && matchesSchedule && matchesAppointmentStatus
    })
  }, [appointment, deferredSearchValue, facilities, filterData.appointmentStatus, filterData.date, filterData.facility, filterData.providers, filterData.scheduleDate, filterData.status, filteredPatientData])

  const visibleAppointments = useMemo(() => {
    if (!Array.isArray(appointment) || appointment.length === 0 || updatedFilteredPatients.length === 0) {
      return []
    }

    const filteredPatientsById = new Map(updatedFilteredPatients.map((patient) => [patient.PatientID, patient]))
    const normalizedScheduleDate = normalizeDateValue(filterData.scheduleDate)

    return appointment.reduce((result, appt) => {
      const patientID = appt?.current?.Patient?.value
      const patient = filteredPatientsById.get(patientID)

      if (!patient) return result

      const status = appt?.data?.Status
      const scheduleBy = appt?.data?.ScheduleBy
      const type = appt?.data?.Type
      const isInFacility = appt?.data?.isInFacility
      const priorAuth = appt?.data?.otherDetails?.priorAuth?.isRequired
      const priorAuthTime = priorAuth ? parseInt(appt?.data?.otherDetails?.priorAuth?.details?.noOfTimes || '0', 10) : 0
      const appointmentScheduleDate = appt?.data?.StartTime?.split?.(' ')?.[0]
      const noteID = appt?.data?.NoteID
      const matchesScheduleDate = !normalizedScheduleDate || normalizeDateValue(appointmentScheduleDate) === normalizedScheduleDate
      const hasCreatedNote = noteID !== null && noteID !== undefined && noteID !== ''

      const isManualOrFollowUp = scheduleBy !== 'auto' || (type === 'Follow Up' && isInFacility && priorAuth && priorAuthTime > 0)
      const isScheduledOrRescheduled = status === 'Scheduled' || status === 'Rescheduled'

      if (!isScheduledOrRescheduled || !isManualOrFollowUp || !matchesScheduleDate || !hasCreatedNote) return result

      result.push({
        patient,
        providerName: appt?.current?.Provider?.label,
        facilityName: patient?.FacilityName || appt?.current?.Facility?.label || appt?.data?.Facility,
        scheduleDate: appointmentScheduleDate,
        appointmentType: type,
        appointmentStatus: status,
        noteID,
      })

      return result
    }, [])
  }, [appointment, filterData.scheduleDate, updatedFilteredPatients])

  const visiblePatients = useMemo(() => {
    const patientMap = new Map()

    visibleAppointments.forEach(({ patient }) => {
      if (!patient?.PatientID) return
      patientMap.set(patient.PatientID, patient)
    })

    return Array.from(patientMap.values())
  }, [visibleAppointments])

  const patientInfoCount = useMemo(
    () => ({
      followUp: visiblePatients.filter((item) => item?.Status === 'Follow Up').length,
      newPatient: visiblePatients.filter((item) => item?.Status === 'NewPatient').length,
    }),
    [visiblePatients],
  )

  const patientCount = visiblePatients.length

  const groupedVisibleAppointments = useMemo(() => {
    const groupedData = {}
    const hasSearchValue = Boolean(deferredSearchValue.trim())
    const providersToShow = filterData.providers.length > 0
      ? providerOptions.filter((p) => filterData.providers.includes(p.value))
      : providerOptions

    // If there's a scheduleDate and no active search, pre-populate that date with all providers
    if (filterData.scheduleDate && !hasSearchValue) {
      const date = filterData.scheduleDate
      groupedData[date] = {}
      providersToShow.forEach((p) => {
        const name = p.value
        groupedData[date][name] = {
          count: 0,
          facilities: {},
        }
      })
    }

    visibleAppointments.forEach((appointmentItem) => {
      const date = appointmentItem?.scheduleDate || 'Unknown Date'
      const providerName = appointmentItem?.providerName || 'Unknown Provider'
      const facilityName = appointmentItem?.facilityName || 'No Facility Available'

      groupedData[date] ||= {}

      // If no active search, ensure all providers are initialized for this date too
      if (!hasSearchValue) {
        providersToShow.forEach((p) => {
          const name = p.value
          groupedData[date][name] ||= {
            count: 0,
            facilities: {},
          }
        })
      }

      groupedData[date][providerName] ||= {
        count: 0,
        facilities: {},
      }
      groupedData[date][providerName].facilities[facilityName] ||= []
      groupedData[date][providerName].facilities[facilityName].push(appointmentItem)
      groupedData[date][providerName].count += 1
    })

    // Sort providers within each date alphabetically
    const sortedGroupedData = {}
    Object.keys(groupedData).forEach((date) => {
      const sortedProviders = Object.fromEntries(
        Object.entries(groupedData[date]).sort(([nameA], [nameB]) =>
          nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
        )
      )
      sortedGroupedData[date] = sortedProviders
    })

    return sortedGroupedData
  }, [visibleAppointments, filterData.scheduleDate, filterData.providers, providerOptions, deferredSearchValue])

  return (
    <>
      <CRow>
        <div className="patient-theme-page">
          <div className="patient-page-wrapper page-wrapper patient-theme-shell dashboard-dark-theme call-center-theme">
            <Pageloader isLoading={isPageLoading} />
            <div className="patient-theme-toolbar-shell">
              <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                <div className="page-header">
                  <div className="d-flex align-items-center gap-2 patient-theme-toolbar flex-wrap">
                    <div className="search-input search-dark patient-theme-search ui-search-control">
                      <SearchIcon />
                      <input
                        className="call-center-search-field"
                        onChange={(e) => {
                          const searchValue = e.target.value
                          setFilterData((previous) => ({ ...previous, searchValue }))
                        }}
                        type="text"
                        placeholder="Search by Patient"
                      />
                    </div>
                    <div className="position-relative ">
                      <Button className="d-flex align-items-center gap-2 patient-theme-filter-button" variant="outlined" size="large" onClick={toggleFilter}>
                        <FunnelPlotOutlined />
                        Filter
                      </Button>

                      <CallCenterFilterPanel
                        showFilter={showFilter}
                        facilities={facilities}
                        facilityOptionsById={facilityOptionsById}
                        filterData={filterData}
                        onClose={closeFilter}
                        onFilterChange={handleFilterChange}
                        providerOptions={providerOptions}
                        providerOptionsByName={providerOptionsByName}
                      />
                    </div>
                    <div className="patient-theme-date-field call-center-toolbar-date">
                      <CustomDatePickingFormat
                        value={filterData.scheduleDate || ''}
                        aria-describedby="callCenterScheduleDate"
                        onChange={(scheduleDate) => {
                          handleFilterChange((previous) => ({ ...previous, scheduleDate }))
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="display-total-patient">
                    <span className="mx-1">{patientInfoCount?.newPatient || 0}</span> NP
                  </div>
                  <div className="display-total-patient">
                    <span className="mx-1">{patientInfoCount?.followUp || 0}</span> FU
                  </div>
                  <div className="display-total-patient">
                    <span className="mx-1">{patientCount}</span> Patients
                  </div>
                </div>
              </div>
            </div>
            <div>
              {!isPageLoading && <CallCenterAppointmentList groupedAppointments={groupedVisibleAppointments} patientClick={patientClick} />}

              <div>{!isPageLoading && Object.keys(groupedVisibleAppointments).length === 0 && <h6 className="d-flex justify-content-center text-secondary">No Patient found</h6>}</div>
            </div>
          </div>
        </div>
      </CRow>

      {modalVisible && <Modal open={modalVisible} type="table" title="" value="" data={modalData}></Modal>}
    </>
  )
}

CallCenter.propTypes = {
  patientClick: PropTypes.func,
}
